import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabConcurrents from '../tabs/TabConcurrents';
import { apiGet } from '../utils/api-client';
import { pickConcurrentEmoji } from '../utils/concurrentEmoji';

/**
 * TabConcurrentsContainer
 *
 * Endpoints branchés :
 *  - GET /api/dashboard/                     → restaurant + RPS du resto courant
 *  - GET /api/places/competitors?placeId=X  → liste des concurrents proches via Google Places
 */
export default function TabConcurrentsContainer() {
  const { t } = useTranslation('tabs');
  const [position, setPosition]       = useState(0);
  const [total, setTotal]             = useState(0);
  const [rps, setRps]                 = useState(0);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [restaurant, setRestaurant]   = useState(null);

  /** Texte de meta sous le nom du concurrent. */
  const metaFor = useCallback((c) => {
    if (c.isYou) return t('concurrents.meta_you_revyo', { count: c.reviews });
    if (!c.reviews) return t('concurrents.meta_no_reviews');
    if (c.rating < 3.5) return t('concurrents.meta_declining', { count: c.reviews });
    if (c.rating >= 4.5) return t('concurrents.meta_strong', { count: c.reviews });
    return t('concurrents.meta_no_tool', { count: c.reviews });
  }, [t]);

  const loadConcurrents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Récup le restaurant courant + son RPS
      const dash = await apiGet('/api/dashboard/');
      const r = dash?.restaurant || {};
      setRestaurant(r);
      setRps(r.rps_score ?? 0);

      const placeId = r.google_place_id;
      if (!placeId) {
        setCompetitors([]);
        setPosition(0);
        setTotal(0);
        setError(t('concurrents.alerts.no_place_id'));
        return;
      }

      // 2. Fetch les concurrents
      const data = await apiGet(`/api/places/competitors?placeId=${encodeURIComponent(placeId)}&radius=1500&max=5`);
      const list = (data?.competitors || []).map((c) => ({
        rank:    c.rank,
        name:    c.name,
        emoji:   pickConcurrentEmoji(c, r),
        rating:  c.rating,
        reviews: c.reviews,
        meta:    metaFor(c),
        isYou:   c.isYou,
        warn:    c.rating > 0 && c.rating < 3.5,
      }));
      setCompetitors(list);
      setPosition(data?.position ?? 0);
      setTotal(data?.total ?? list.length);
    } catch (e) {
      console.error('[TabConcurrents] load error:', e);
      // Cas spécial : Place ID invalide côté Google
      if (e.payload?.code === 'PLACE_ID_INVALID' || e.status === 422) {
        setError(e.payload?.message || t('concurrents.alerts.invalid_place_id'));
      } else {
        setError(e.message || t('concurrents.alerts.load_failed'));
      }
    } finally {
      setLoading(false);
    }
  }, [metaFor, t]);

  useEffect(() => { loadConcurrents(); }, [loadConcurrents]);

  /** Refresh au click bouton dédié. */
  const handleRefresh = useCallback(() => loadConcurrents(), [loadConcurrents]);

  /** Click sur un bouton d'action (ex: "Améliorer le taux de réponse"). */
  const handleActionClick = useCallback((id) => {
    if (id === 'response-rate') {
      window.location.href = '/repondre';
    } else if (id === 'sync-google') {
      window.location.href = '/configuration';
    } else if (id === 'collecte') {
      window.location.href = '/collecte';
    }
  }, []);

  return (
    <>
      {error && (
        <div style={{
          background: '#FEF3C7',
          border: '1px solid #F5DBA0',
          borderRadius: 12,
          padding: '14px 18px',
          fontSize: 13,
          color: '#92580A',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>⚠️ {error}</div>
          <button
            type="button"
            onClick={() => { window.location.href = '/configuration'; }}
            style={{
              background: '#E8502A',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            → Configuration
          </button>
        </div>
      )}
      <TabConcurrents
        position={position}
        total={total}
        rps={rps}
        // On passe TOUJOURS le tableau réel, même vide : c'est ce `undefined`
        // conditionnel qui déclenchait la valeur par défaut fictive du
        // composant (classement de restaurants inventés).
        competitors={competitors}
        area={restaurant?.ville || ''}
        city={restaurant?.ville || ''}
        liveData={competitors.length > 0}
        strengths={undefined}      // section masquée tant que pas de calcul réel
        opportunities={undefined}  // section masquée tant que pas d'IA branchée
        onRefresh={handleRefresh}
        onActionClick={handleActionClick}
      />
      {loading && (
        <div style={{
          position: 'fixed', top: 16, right: 16,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 100, padding: '6px 14px', fontSize: 12,
          color: 'var(--text2)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: 100,
        }}>
          🔄 Chargement…
        </div>
      )}
    </>
  );
}
